import type { Dispatch, SetStateAction } from "react";

import { Trans } from "@similarweb/i18n-provider";

import { TitleWithInfo } from "app/components/TitleWithInfo/TitleWithInfo";
import { translate } from "app/filters/i18nFilter";
import { DESCRIPTION_ID, RT_KNOWLEDGE_BASE_URL } from "app/pages/activation/components/RankWidget/config";
import { CompetitorsDropdown } from "app/pages/activation/components/RankWidget/CreationModal/Components/CompetitorsDropdown/CompetitorsDropdown";
import { CountryDropdown } from "app/pages/activation/components/RankWidget/CreationModal/Components/CountryDropdown/CountryDropdown";
import { SourceDropdown } from "app/pages/activation/components/RankWidget/CreationModal/Components/SourceDropdown/SourceDropdown";
import { creationModalKeys } from "app/pages/activation/components/RankWidget/dataAutomationKeys";
import { modalsTexts } from "app/pages/activation/components/RankWidget/texts";
import type { RTFormData } from "app/pages/activation/components/RankWidget/types";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { RankTrackerCountry } from "app/pages/activation/types/api";

import * as SC from "../../styles";

type InitialStepSchema = {
    domain: string;
    competitors: string[];
    setFormData: Dispatch<SetStateAction<RTFormData>>;
    country?: number;
    availableCountries: RankTrackerCountry[];
    isCountriesLoading?: boolean;
};

export const InitialStep = ({
    competitors,
    domain,
    setFormData,
    country,
    availableCountries,
    isCountriesLoading,
}: InitialStepSchema) => {
    const { initialStep } = creationModalKeys;
    const { rankTracker } = activationTracking;

    const onLinkClick = () => {
        rankTracker.trackClickLearnMore();
    };

    return (
        <>
            <SC.Title>
                <TitleWithInfo
                    title={translate(modalsTexts.firstStepTitle)}
                    tooltip={
                        <div>
                            {translate(modalsTexts.firstStepTooltip)}

                            <SC.TooltipLink
                                href={RT_KNOWLEDGE_BASE_URL}
                                target="_blank"
                            >
                                <span onClick={onLinkClick}>{translate(modalsTexts.firstStepTooltipLink)}</span>
                            </SC.TooltipLink>
                        </div>
                    }
                    fontVariant="h5_2"
                    dataAutomation={initialStep.title}
                />
            </SC.Title>

            <SC.Content
                data-automation={initialStep.description}
                step="initial"
            >
                <p>{translate(modalsTexts.firstStepDescription)}</p>

                <SC.DescriptionWithDD id={DESCRIPTION_ID}>
                    <Trans
                        i18nKey={modalsTexts.firstStepDescription2}
                        values={{ domain }}
                        components={{
                            competitors: (
                                <CompetitorsDropdown
                                    competitors={competitors}
                                    setFormData={setFormData}
                                    dataAutomation={initialStep.competitorsDropdown}
                                />
                            ),
                            country: (
                                <CountryDropdown
                                    country={country}
                                    availableCountries={availableCountries}
                                    setFormData={setFormData}
                                    dataAutomation={initialStep.countryDropdown}
                                    isLoading={isCountriesLoading}
                                />
                            ),
                            source: (
                                <SourceDropdown
                                    setFormData={setFormData}
                                    dataAutomation={initialStep.sourceDropdown}
                                />
                            ),
                        }}
                    />
                </SC.DescriptionWithDD>
            </SC.Content>
        </>
    );
};
